import type { MetaArtifact } from '@ev/shared'
import type { ReactNode } from 'react'
import { useMeta } from '../data/hooks'
import { formatDate, formatDateTime } from '../detail/format'
import { useLocale } from '../detail/useLocale'
import { type Strings, useStrings } from '../i18n'
import { Overlay } from './Overlay'
import { useOverlayStore } from './overlayStore'
import { EIPA_FILES, newestEipaTimestamp, SOURCES, type SourceBlock } from './sources'

/**
 * Attribution, licensing, the terms EIPA data comes with, and how good the
 * current publish actually is.
 *
 * Every number on this page is read from `meta.json` at run time. Hardcoding
 * "200 locations" would be a lie the moment the pipeline ran, and the data
 * quality section exists precisely so a visitor can see how much of the map is
 * derived rather than declared.
 */

interface SourceCopy {
  readonly name: string
  readonly description: string
  readonly licence: string
}

const sourceCopy = (block: SourceBlock, strings: Strings): SourceCopy => {
  switch (block.id) {
    case 'eipa':
      return {
        name: strings.about.eipaName,
        description: strings.about.eipaDescription,
        licence: strings.about.eipaLicence,
      }
    case 'ocm':
      return {
        name: strings.about.ocmName,
        description: strings.about.ocmDescription,
        licence: strings.about.ocmLicence,
      }
    case 'other':
      return {
        name: strings.about.otherName,
        description: strings.about.otherDescription,
        licence: strings.about.otherLicence,
      }
    case 'basemap':
      return {
        name: strings.about.openFreeMapName,
        description: strings.about.openFreeMapDescription,
        licence: strings.about.openFreeMapLicence,
      }
    default:
      return {
        name: strings.about.maplibreName,
        description: strings.about.maplibreDescription,
        licence: strings.about.maplibreLicence,
      }
  }
}

const Stat = ({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) => (
  <div className="ev-stat">
    <dt className="ev-stat__label">{label}</dt>
    <dd className="ev-stat__value">
      {value}
      {hint !== undefined && <span className="ev-stat__hint">{hint}</span>}
    </dd>
  </div>
)

const SourceCard = ({ block, meta }: { block: SourceBlock; meta: MetaArtifact | null }) => {
  const strings = useStrings()
  const locale = useLocale()
  const copy = sourceCopy(block, strings)

  const updated = ((): string | null => {
    if (meta === null || block.freshness === null) return null
    if (block.freshness === 'eipa') return newestEipaTimestamp(meta.sources.eipa)
    return meta.sources.ocm ?? null
  })()

  return (
    <section className="ev-source">
      <h3 className="ev-source__name">
        {/* A source credited without being named has nothing to link to, and a
            dead link would be worse than plain text. */}
        {block.homepage === undefined ? (
          copy.name
        ) : (
          <a href={block.homepage} target="_blank" rel="noreferrer noopener">
            {copy.name}
          </a>
        )}
      </h3>
      <p className="ev-source__description">{copy.description}</p>

      <dl className="ev-source__facts">
        <dt>{strings.about.licence}</dt>
        <dd>{copy.licence}</dd>
        {updated !== null && (
          <>
            <dt>{strings.about.lastUpdated}</dt>
            <dd>{formatDateTime(updated, locale)}</dd>
          </>
        )}
      </dl>

      {block.links.length > 0 && (
        <ul className="ev-source__links">
          {block.links.map((link) => (
            <li key={link.href}>
              <a href={link.href} target="_blank" rel="noreferrer noopener">
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      )}

      {block.id === 'eipa' && meta !== null && (
        <details className="ev-source__details">
          <summary>{strings.about.perFileTimestamps}</summary>
          <dl className="ev-source__facts">
            {EIPA_FILES.map((file) => {
              // A per-file stamp can be absent: a run that could not reach one
              // EIPA file still publishes the other five, and saying so is more
              // use than printing "Invalid Date".
              const stamp = (meta.sources.eipa as Partial<Record<typeof file, string>>)[file]
              return (
                <div key={file} className="ev-source__row">
                  <dt>{strings.eipaSource[file]}</dt>
                  <dd>
                    {stamp === undefined ? strings.common.unknown : formatDateTime(stamp, locale)}
                  </dd>
                </div>
              )
            })}
          </dl>
        </details>
      )}
    </section>
  )
}

const DataQuality = ({ meta }: { meta: MetaArtifact }) => {
  const strings = useStrings()
  const locale = useLocale()
  const { counts, voltageCoverage, ocmMatch, availability } = meta
  const voltageTotal = voltageCoverage.declared + voltageCoverage.derived + voltageCoverage.unknown
  const availabilityTotal =
    availability.free + availability.occupied + availability.unavailable + availability.unknown

  return (
    <>
      <h3 className="ev-about__subheading">{strings.about.countsHeading}</h3>
      <dl className="ev-stats">
        <Stat label={strings.about.sites} value={counts.sites} />
        <Stat label={strings.about.stations} value={counts.stations} />
        <Stat label={strings.about.points} value={counts.points} />
        <Stat label={strings.about.connectors} value={counts.connectors} />
        <Stat label={strings.about.operatorCount} value={counts.operators} />
      </dl>

      <h3 className="ev-about__subheading">{strings.about.voltageHeading}</h3>
      <dl className="ev-stats">
        <Stat
          label={strings.about.declared}
          value={voltageCoverage.declared}
          hint={strings.about.share(voltageCoverage.declared, voltageTotal)}
        />
        <Stat
          label={strings.about.derived}
          value={voltageCoverage.derived}
          hint={strings.about.share(voltageCoverage.derived, voltageTotal)}
        />
        <Stat
          label={strings.about.unknownVoltage}
          value={voltageCoverage.unknown}
          hint={strings.about.share(voltageCoverage.unknown, voltageTotal)}
        />
      </dl>

      <h3 className="ev-about__subheading">{strings.about.matchHeading}</h3>
      <dl className="ev-stats">
        <Stat label={strings.about.ocmCandidates} value={ocmMatch.candidates} />
        <Stat
          label={strings.about.ocmMatched}
          value={ocmMatch.matched}
          hint={strings.about.share(ocmMatch.matched, ocmMatch.candidates)}
        />
        <Stat
          label={strings.about.ocmUnmatched}
          value={ocmMatch.unmatched}
          hint={strings.about.share(ocmMatch.unmatched, ocmMatch.candidates)}
        />
        <Stat
          label={strings.about.ocmMatchRate}
          value={new Intl.NumberFormat(locale, {
            style: 'percent',
            maximumFractionDigits: 1,
          }).format(ocmMatch.matchRate)}
        />
      </dl>

      <h3 className="ev-about__subheading">{strings.about.availabilityHeading}</h3>
      <dl className="ev-stats">
        <Stat
          label={strings.availability.free}
          value={availability.free}
          hint={strings.about.share(availability.free, availabilityTotal)}
        />
        <Stat
          label={strings.availability.occupied}
          value={availability.occupied}
          hint={strings.about.share(availability.occupied, availabilityTotal)}
        />
        <Stat
          label={strings.availability.unavailable}
          value={availability.unavailable}
          hint={strings.about.share(availability.unavailable, availabilityTotal)}
        />
        <Stat
          label={strings.availability.unknown}
          value={availability.unknown}
          hint={strings.about.share(availability.unknown, availabilityTotal)}
        />
      </dl>

      <dl className="ev-stats ev-stats--wide">
        <Stat label={strings.about.generated} value={formatDateTime(meta.generated, locale)} />
        <Stat label={strings.about.pipelineVersion} value={meta.pipelineVersion} />
      </dl>
    </>
  )
}

export const AboutPage = () => {
  const strings = useStrings()
  const locale = useLocale()
  const close = useOverlayStore((state) => state.close)
  const open = useOverlayStore((state) => state.open)
  const { resource, reload } = useMeta()
  const meta = resource.status === 'ready' ? resource.value : null

  return (
    <Overlay title={strings.about.title} onClose={close}>
      <p className="ev-about__intro">{strings.about.intro}</p>

      <h2 className="ev-about__heading">{strings.about.sourcesHeading}</h2>
      <div className="ev-sources">
        {SOURCES.map((block) => (
          <SourceCard key={block.id} block={block} meta={meta} />
        ))}
      </div>

      <h2 className="ev-about__heading">{strings.about.termsHeading}</h2>
      <p>{strings.about.termsIntro}</p>
      <ol className="ev-about__terms">
        {strings.about.terms.map((term) => (
          <li key={term}>{term}</li>
        ))}
      </ol>
      <p className="ev-about__note">{strings.about.termsCompliance}</p>
      <p className="ev-about__note">{strings.about.termsAccess}</p>
      {meta !== null && (
        <p className="ev-about__note">
          {`${strings.about.generated}: ${formatDate(meta.generated, locale)}`}
        </p>
      )}
      <p className="ev-about__disclaimer">{strings.about.disclaimer}</p>

      <h2 className="ev-about__heading">{strings.about.qualityHeading}</h2>
      <p>{strings.about.qualityIntro}</p>

      {resource.status === 'loading' && <p className="ev-about__note">{strings.common.loading}</p>}

      {resource.status === 'error' && (
        <p className="ev-about__note" role="alert">
          {strings.about.loadFailed}{' '}
          <button type="button" className="ev-button ev-button--quiet" onClick={reload}>
            {strings.common.retry}
          </button>
        </p>
      )}

      {meta !== null && <DataQuality meta={meta} />}

      <p className="ev-about__note">
        <button
          type="button"
          className="ev-button ev-button--quiet"
          onClick={() => open('methodology')}
        >
          {strings.voltage.methodologyOpen}
        </button>
      </p>
    </Overlay>
  )
}
